import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fetchText = async (ref) => {
  const { data } = await axios.get(`${API}/texts`, { params: { ref } });
  return data;
};

const sefariaUrl = (ref) => `https://www.sefaria.org/${encodeURIComponent(ref.replace(/ /g, "_"))}`;

export const SefariaPassage = ({ passage, index }) => {
  const { ref, label, commentary } = passage;
  const { data, isLoading, isError } = useQuery({
    queryKey: ["sefaria", ref],
    queryFn: () => fetchText(ref),
    staleTime: Infinity,
    retry: 1,
  });

  const count = Math.max(data?.hebrew?.length ?? 0, data?.english?.length ?? 0);

  return (
    <article
      data-testid={`passage-${index + 1}`}
      className="border-t hairline py-12 lg:py-16"
    >
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
        <header className="lg:col-span-3">
          <span className="font-mono text-[10px] tracking-[0.22em] text-terracotta">
            {String(index + 1).padStart(2, "0")}
          </span>
          <h3 className="mt-3 font-serif text-2xl leading-snug text-ink sm:text-3xl">{label}</h3>
          <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.18em] text-ink-muted">{ref}</p>
          <a
            href={sefariaUrl(ref)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`passage-sefaria-link-${index + 1}`}
            className="link-underline mt-4 inline-flex items-center gap-1.5 text-xs text-ink-soft hover:text-ink"
          >
            Open on Sefaria <ExternalLink size={11} />
          </a>
        </header>

        <div className="lg:col-span-9">
          {isLoading && (
            <div data-testid={`passage-loading-${index + 1}`} className="space-y-3">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-5 animate-pulse rounded bg-ink/5" style={{ width: `${90 - i * 12}%` }} />
              ))}
            </div>
          )}

          {isError && (
            <p data-testid={`passage-error-${index + 1}`} className="text-sm text-ink-muted">
              The text could not be loaded right now.{" "}
              <a href={sefariaUrl(ref)} target="_blank" rel="noopener noreferrer" className="underline">
                Read it on Sefaria
              </a>
              .
            </p>
          )}

          {data && (
            <div data-testid={`passage-text-${index + 1}`} className="space-y-6">
              {Array.from({ length: count }, (_, i) => (
                <div key={i} className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-8">
                  <p dir="rtl" lang="he" className="font-serif text-xl leading-[1.9] text-ink sm:text-2xl">
                    {data.hebrew[i] ?? ""}
                  </p>
                  <p className="text-sm leading-[1.8] text-ink-soft sm:text-base">{data.english[i] ?? ""}</p>
                </div>
              ))}
              {data.english.length === 0 && (
                <p className="text-sm text-ink-muted">No English translation is available for this passage.</p>
              )}
              {(data.versionTitles?.he || data.versionTitles?.en) && (
                <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-ink-muted/70">
                  {[data.versionTitles.he, data.versionTitles.en].filter(Boolean).join(" · ")} · via Sefaria
                </p>
              )}
            </div>
          )}

          <div className="mt-8 border-l-2 border-terracotta pl-5 sm:pl-6">
            <p className="eyebrow">Commentary</p>
            <p className="mt-3 max-w-2xl text-sm leading-[1.8] text-ink-soft sm:text-base">{commentary}</p>
          </div>
        </div>
      </div>
    </article>
  );
};
